/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { Attachment } from '@vendorflow/types'
import {
  AudioFile as AudioFileIcon,
  BrokenImage as BrokenImageIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material'
import { useAttachmentHref } from 'queries/Attachments.queries'
import { useCallback, useEffect, useState } from 'react'
import PdfThumbnail from 'components/PdfViewer/PdfThumbnail'

const thumbnailStyle = css`
  max-height: 120px;
  width: 5rem;
  height: auto;
`

export default function AssetThumbnail({ className, attachment }: { className?: string; attachment: Attachment }) {
  const { ref, mimeType } = attachment
  const href = useAttachmentHref(ref)
  const [elementRef, setElementRef] = useState<any>(null)
  const [height, setHeight] = useState('auto')
  const isPdf = mimeType === 'application/pdf'
  const resizeCallback = useCallback((entries: ResizeObserverEntry[]) => {
    entries.forEach((entry) => {
      setHeight(entry.contentRect.height + 'px')
    })
  }, [])

  useEffect(() => {
    const observer = new ResizeObserver(resizeCallback)
    if (elementRef) {
      observer.observe(elementRef)
    }

    return () => observer.disconnect()
  }, [elementRef])

  function renderThumbnailBasedOnMimeType() {
    if (href.isInitialLoading) {
      return null
    }
    if (!href.data || !mimeType) {
      return (
        <BrokenImageIcon
          ref={setElementRef}
          css={css`
            width: 5rem;
            height: auto;
          `}
          fontSize="large"
        />
      )
    }
    if (mimeType.includes('image')) {
      return (
        <img
          crossOrigin="anonymous"
          ref={setElementRef}
          alt={attachment.filename}
          css={thumbnailStyle}
          src={href.data}
        />
      )
    }
    if (mimeType.includes('audio')) {
      return <AudioFileIcon ref={setElementRef} css={thumbnailStyle} fontSize="large" />
    }
    if (mimeType.includes('video')) {
      return <video crossOrigin="anonymous" ref={setElementRef} css={thumbnailStyle} src={href.data} />
    }
    if (mimeType === 'application/pdf') {
      return <PdfThumbnail src={href.data} />
    }

    return <UploadFileIcon ref={setElementRef} css={thumbnailStyle} fontSize="large" />
  }

  return (
    <div
      css={css`
        height: ${height};
        max-height: 120px;
        width: ${isPdf ? '64px' : 'auto'};
      `}
      className={className}
    >
      {renderThumbnailBasedOnMimeType()}
    </div>
  )
}
